import { useMemo, useState } from 'react';
import type { ItineraryDay, TripDestination } from '../types';
import { legIndexForDay } from '../utils/tripLegs';

interface Options {
  days: ItineraryDay[];
  legs: readonly TripDestination[];
  enabledDays: Set<number>;
  /** `useUnifiedRoutes.showOnlyDays`: pure state, never a purchase (ADR-0025). */
  showOnlyDays: (idxs: number[]) => void;
  /** Frames the map on the days just shown. */
  onFrame: (points: { lat: number; lng: number }[]) => void;
}

const sameSet = (idxs: number[], set: Set<number>) => idxs.length === set.size && idxs.every(i => set.has(i));

/**
 * The destination chips of the routes view (ADR-0030). A leg is the days with
 * stops it covers; picking it shows exactly those. It reads as picked only while
 * the day chips still say so — turning one more day on un-picks it, no extra state.
 */
export function useRouteLegFilter({ days, legs, enabledDays, showOnlyDays, onFrame }: Options) {
  const [picked, setPicked] = useState<number | null>(null);

  const routedIdxs = useMemo(
    () => days.flatMap((d, i) => (d.activities.length > 0 ? [i] : [])),
    [days],
  );
  const dayIdxsByLeg = useMemo(
    () => legs.map((_, li) => routedIdxs.filter(i => legIndexForDay(legs, days[i].dayNumber) === li)),
    [legs, routedIdxs, days],
  );

  const active = picked !== null && dayIdxsByLeg[picked] && sameSet(dayIdxsByLeg[picked], enabledDays) ? picked : null;

  function select(legIdx: number) {
    const idxs = legIdx === active ? routedIdxs : dayIdxsByLeg[legIdx] ?? [];
    setPicked(legIdx === active ? null : legIdx);
    showOnlyDays(idxs);
    onFrame(idxs.flatMap(i => days[i].activities.flatMap(a =>
      a.latitude == null || a.longitude == null ? [] : [{ lat: Number(a.latitude), lng: Number(a.longitude) }],
    )));
  }

  return { active, select };
}
